import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable, catchError, switchMap, take, throwError } from 'rxjs';

import { KeycloakPasswordAuthService } from './keycloak-password-auth.service';
import { RETURN_URL_KEY, clearMockAuthed } from './keycloak-auth.guard';

/**
 * Handles an access token that expires / is revoked MID-SESSION — the case the
 * up-front auth guard can't catch (token was valid at page-load, dies while the
 * tab stays open, or the backend's audience / issuer check changes).
 *
 * On a `401 Unauthorized` from a `/api/*` call:
 *
 *   1. **Silent refresh** — if a ROPC/password session with a still-valid
 *      refresh token exists, rotate the token and replay the original request
 *      once with the fresh bearer. The user never notices ("keep me signed in").
 *   2. **Sign out** — if there's nothing to refresh (refresh token also dead,
 *      an OIDC-only session, or the replay STILL 401s) the session is treated
 *      as gone: every local auth trace is cleared and the user is redirected to
 *      the local sign-in page, with the current URL saved so post-login returns
 *      them where they were.
 *
 * This unifies on `/signin` — the same destination {@link keycloakAuthGuard}
 * uses — instead of the previous straight-to-Keycloak `oidc.authorize()`, so
 * password, OTP and SSO users all land on one predictable page.
 *
 * Order matters: register this AFTER the bearer interceptor so the bearer is
 * attached on the outbound request; the response stream flows back through here.
 */

/** Marks a request already replayed with a refreshed token, so a second 401
 *  doesn't loop — it drops straight through to the sign-in redirect. */
const RETRIED = new HttpContextToken<boolean>(() => false);

export const keycloak401Interceptor: HttpInterceptorFn = (req, next) => {
  return next(req).pipe(
    catchError((err) => {
      if (!(err instanceof HttpErrorResponse) || err.status !== 401 || !isApiCall(req.url)) {
        return throwError(() => err);
      }

      const password = inject(KeycloakPasswordAuthService);
      const oidc = inject(OidcSecurityService);
      const router = inject(Router);

      const alreadyRetried = req.context.get(RETRIED);

      // A request without a bearer cannot have failed because its bearer
      // expired. This is important for anonymous background calls made by
      // root-scoped stores while /signin is loading (notably /issues/mine).
      // Let the originating API client handle that 401; do not start a
      // refresh/redirect loop.
      if (!alreadyRetried && !req.headers.has('Authorization')) {
        return throwError(() => err);
      }

      // The sign-in page is public and root-scoped stores may still issue
      // background API calls there. Never turn one of those 401s into an
      // "expired session" redirect; OTP/password verification owns its own
      // error handling while the user is signing in.
      if (!alreadyRetried && router.url.startsWith('/signin')) {
        return throwError(() => err);
      }

      // Some root stores hydrate optimistically while the public sign-in
      // screen is open (for example IssuesStore calls /issues/mine at startup).
      // Its expected anonymous 401 must not be interpreted as an expired
      // authenticated session, otherwise the interceptor redirects back to
      // /signin before OTP verification can finish.
      if (!alreadyRetried && !password.sessionAccessToken() && !password.canRefresh()) {
        return oidc.isAuthenticated$.pipe(
          take(1),
          switchMap(({ isAuthenticated }) => {
            if (!isAuthenticated) return throwError(() => err);
            return oidc.forceRefreshSession().pipe(
              switchMap((resp) => {
                if (!resp?.accessToken) return expireToSignIn(err, password, oidc, router);
                return next(withBearer(req, resp.accessToken));
              }),
              catchError(() => expireToSignIn(err, password, oidc, router)),
            );
          }),
        );
      }

      // Fresh refresh token available and this is the FIRST failure → rotate
      // the token silently and replay the original request once.
      if (!alreadyRetried && password.canRefresh()) {
        return password.refresh().pipe(
          switchMap((resp) => next(withBearer(req, resp.access_token))),
          // Refresh itself failed (token revoked / expired despite the check) →
          // give up and sign the user out.
          catchError(() => expireToSignIn(err, password, oidc, router)),
        );
      }

      // SSO-only session: let the OIDC client rotate its own refresh token
      // before we give up. Without this, a mid-session expiry on the SSO path
      // drops the user to /signin even though Keycloak can still refresh.
      if (!alreadyRetried) {
        return oidc.isAuthenticated$.pipe(
          take(1),
          switchMap(({ isAuthenticated }) => {
            if (!isAuthenticated) return expireToSignIn(err, password, oidc, router);
            return oidc.forceRefreshSession().pipe(
              switchMap((resp) => {
                if (!resp?.accessToken) return expireToSignIn(err, password, oidc, router);
                return next(withBearer(req, resp.accessToken));
              }),
              catchError(() => expireToSignIn(err, password, oidc, router)),
            );
          }),
        );
      }

      // Nothing to recover with (or the retry still 401'd) → sign out.
      return expireToSignIn(err, password, oidc, router);
    }),
  );
};

/**
 * Attach the rotated token explicitly. The bearer interceptor runs BEFORE this
 * one, so it won't re-stamp the header on replay; we also flag the request so a
 * second 401 short-circuits to sign-out instead of refreshing again.
 */
function withBearer(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({
    setHeaders: { Authorization: `Bearer ${token}` },
    context: req.context.set(RETRIED, true),
  });
}

/** Guards against a burst of parallel 401s each firing its own navigation. */
let redirecting = false;

function expireToSignIn(
  err: unknown,
  password: KeycloakPasswordAuthService,
  oidc: OidcSecurityService,
  router: Router,
): Observable<never> {
  // Remember where the user was so post-login can send them back (skip when
  // we're already on / heading to the sign-in page to avoid a self-return).
  try {
    if (!router.url.startsWith('/signin')) {
      sessionStorage.setItem(RETURN_URL_KEY, router.url);
    }
  } catch {
    /* private mode / quota — fall back to /dashboard after login */
  }

  // Wipe every local auth trace so the guard can't mistake the dead session
  // for a live one on the next navigation.
  password.logout();
  clearMockAuthed();
  try {
    oidc.logoffLocal();
  } catch {
    /* no OIDC session to clear — harmless */
  }

  if (!redirecting) {
    redirecting = true;
    void router
      .navigate(['/signin'], { queryParams: { reason: 'expired' } })
      .finally(() => { redirecting = false; });
  }
  return throwError(() => err);
}

function isApiCall(url: string): boolean {
  // Same scoping as the bearer interceptor: only backend API calls. Keycloak's
  // own token-endpoint 401s (refresh failures) are absolute URLs, never /api/*,
  // so they can't recurse through here.
  return url.startsWith('/api/');
}
